import { MAPS, SPACE_IDS, type GameState, type SpaceId } from '@cc/shared';
import { CoinSVG } from './CoinSVG';

const CENTERS: Record<SpaceId, [number, number]> = {
  '1': [160, 90], '2': [280, 90], '3': [400, 90],
  '4': [100, 194], '5': [220, 194], '6': [340, 194], '7': [460, 194],
  '8': [160, 298], '9': [280, 298], '10': [400, 298],
};

function points(cx: number, cy: number, radius = 67): string {
  return Array.from({ length: 6 }, (_, index) => {
    const angle = Math.PI / 180 * (60 * index - 30);
    return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
  }).join(' ');
}

export function HexBoard({ state, eligible = [], selected, onSpace, onInspect, disabled = false, thumbnail = false }: {
  state: GameState;
  eligible?: SpaceId[];
  selected?: SpaceId | undefined;
  onSpace?: (spaceId: SpaceId) => void;
  onInspect?: (spaceId: SpaceId) => void;
  disabled?: boolean;
  thumbnail?: boolean;
}) {
  const map = MAPS[state.mapId];
  const eligibleSet = new Set(eligible);
  return (
    <div className={`board-shell ${thumbnail ? 'board-shell--thumbnail' : ''}`} style={{ background: map.boardBackground }}>
      <svg className="hex-board" viewBox="25 15 510 365" role="group" aria-label={`${map.name} game board`}>
        {map.spaces.map((space) => {
          const [cx, cy] = CENTERS[space.id];
          const region = map.regions.find((item) => item.id === space.regionId)!;
          const stack = state.board[space.id];
          const top = stack.at(-1);
          const actionable = !disabled && eligibleSet.has(space.id);
          const label = `${space.name} — ${top ? `controlled by ${top.side}, stack of ${stack.length}` : 'empty'}`;
          return (
            <g
              key={space.id}
              className={`hex ${actionable ? 'hex--eligible' : ''} ${selected === space.id ? 'hex--selected' : ''}`}
              role={actionable ? 'button' : undefined}
              tabIndex={actionable ? 0 : -1}
              aria-label={label}
              onClick={() => actionable && onSpace?.(space.id)}
              onKeyDown={(event) => { if (actionable && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onSpace?.(space.id); } }}
            >
              <polygon className="hex__shape" points={points(cx, cy)} fill={region.fill} stroke="#fff" />
              {!thumbnail && <polygon className="hex__selection" points={points(cx, cy, 71)} fill="none" />}
              {stack.slice(-4).map((coin, index, visible) => {
                const size = 38 + coin.rank * 5;
                const depthOffset = (visible.length - index - 1) * 5;
                return <g key={coin.uid} className="board-coin" transform={`translate(${cx - size / 2} ${cy - size / 2 + depthOffset})`} opacity={index === visible.length - 1 ? 1 : Math.max(0.55, 0.88 - depthOffset * 0.03)}><CoinSVG rank={coin.rank} side={coin.side} size={size} /></g>;
              })}
              {!thumbnail && stack.length >= 2 && <g className="stack-info" role="button" tabIndex={0} aria-label={`Inspect stack on ${space.name}`} onClick={(event) => { event.stopPropagation(); onInspect?.(space.id); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') onInspect?.(space.id); }}><circle cx={cx + 47} cy={cy - 38} r="13" fill="#fff" stroke={region.stroke} strokeWidth="2"/><circle cx={cx + 47} cy={cy - 44} r="1.8" fill={region.stroke}/><path d={`M ${cx + 47} ${cy - 40} v 8`} stroke={region.stroke} strokeWidth="2.8" strokeLinecap="round"/></g>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function MiniBoard({ mapId }: { mapId: GameState['mapId'] }) {
  const empty = Object.fromEntries(SPACE_IDS.map((id) => [id, []])) as unknown as GameState['board'];
  const state = { mapId, board: empty } as GameState;
  return <HexBoard state={state} thumbnail disabled />;
}
